import { useEffect, useState } from 'react';
import PitchAnimation from './PitchAnimation';
import IntensityAnimation from './IntensityAnimation';
import MusicNotationView from './MusicNotationView';
import ColorCodedWordsView from './ColorCodedWordsView';
import PitchIntensityAnimation from './PitchIntensityAnimation';
import axios from 'axios';
import { defaultColormap } from './colormaps';

// Icons
const MusicIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" />
  </svg>
);

const ColorsIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
  </svg>
);

const PitchIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12m0 0l-5-5m5 5l5-5" />
  </svg>
);

const IntensityIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
  </svg>
);

const CombinedIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

export default function NarratorIntonation({
  subject,
  file,
  segment,
  broadcastTime,
  view,
  onWordClick, // 👈 add
}) {
  const [selectedColormap, setSelectedColormap] = useState(defaultColormap || 'speechStable');
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLegend, setShowLegend] = useState(true);

  useEffect(() => {
    const getIntonation = async () => {
      setLoading(true);
      const token = localStorage.getItem('token');
      const url = `${process.env.REACT_APP_USER_API_URL}/analysis/narrator/${subject.owner}/${subject.language}/${subject.author}/${subject.title}/${file.file}/${segment.start}/${segment.end}/${subject.type}`;

      try {
       const formData = new FormData();
formData.append('text', segment?.text || '');

const response = await axios.post(url, formData, {
  headers: {
    Authorization: `Bearer ${token}`,
  },
});
        setAssessment(response.data);
      } catch (err) {
        console.error('Error loading intonation:', err);
      } finally {
        setLoading(false);
      }
    };

    getIntonation();
  }, [subject, file, segment]);

  // Get the main data object
  const narratorIntonation = assessment?.narrator_intonation;
  
  // Handle both possible data structures:
  let intensityData, pitchData, pitch1stDerivativeData;

  
  if (narratorIntonation?.intensity?.intensity) {
    // Nested structure: narrator_intonation.intensity contains both intensity and pitch
    intensityData = narratorIntonation.intensity.intensity;
    pitchData = narratorIntonation.intensity.pitch;
    pitch1stDerivativeData = narratorIntonation.intensity.pitch_1st_derivative;
  } else {
    // Original structure: intensity and pitch are direct children
    intensityData = narratorIntonation?.intensity;
    pitchData = narratorIntonation?.pitch;
    pitch1stDerivativeData = narratorIntonation?.pitch_1st_derivative;
  }

  const words = segment?.words || [];

  console.log('🎵 Final Data Check:', {
    intensityData: intensityData,
    pitchData: pitchData,
    pitch1stDerivativeData: pitch1stDerivativeData,
    hasIntensityXs: !!intensityData?.xs,
    hasPitchXs: !!pitchData?.xs,
    hasDerivative: !!pitch1stDerivativeData?.ys
  });

return (
    <div className="flex flex-col flex-1 space-y-3">
      {/* Loading */}
      {loading && <div>Loading intonation...</div>}

      {/* Plot area */}
      {!loading && !assessment && <div>No intonation data available.</div>}

      {!loading && assessment && (
        <>
          {/* All view components use the 'view' prop from parent */}
          {view === 'pitch' && pitchData && (
            <PitchAnimation
              intensity={intensityData}
              pitch={pitchData}
              onWordClick={onWordClick} 
              segment={segment}
              broadcastTime={broadcastTime}
            />
          )}

          {view === 'intensity' && intensityData && (
            <IntensityAnimation
              intensity={intensityData}
              segment={segment}
              onWordClick={onWordClick}
              broadcastTime={broadcastTime}
            />
          )}

          {view === 'music' && pitchData && (
            <MusicNotationView 
              pitch={pitchData}
              segment={segment}
              pitch1stDerivative={pitch1stDerivativeData} 
              intensity={intensityData}
              broadcastTime={broadcastTime}
              showWordLabels={true}
            />
          )}

          {view === 'colors' && pitchData && (
            <ColorCodedWordsView 
              words={words} 
              pitch={pitchData}
              intensity={intensityData}
              segment={segment}
              broadcastTime={broadcastTime}
              showLegend={showLegend}
              onToggleLegend={() => setShowLegend(!showLegend)}
            />
          )}

          {view === 'combined' && intensityData && pitchData && (
  <PitchIntensityAnimation 
    intensity={intensityData}
    pitch={pitchData}
    segment={segment}
    broadcastTime={broadcastTime}
    selectedColormap={selectedColormap}
    onColormapChange={setSelectedColormap}
    onWordClick={onWordClick}
  />
)}
        </>
      )}
    </div>
  );
}